import { EventEmitter, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { EndPoint } from '../config/endpoint';
import { Classroom } from '../interface/calendar';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class StudentService {
  isExpand = new EventEmitter<object>();
  currentClassroom = new EventEmitter<Classroom>();
  studentHelpOptionChange = new EventEmitter<any>();

  constructor(private httpService: HttpService) {}

  public getCurrentMinutesAndSecond(time: string): string | undefined {
    try {
      if (!time) return;
      const timeArray = time.split(' ');

      const minutes = parseInt(timeArray[0], 10);
      const seconds = parseInt(timeArray[2], 10);

      return (minutes < 10 ? '0' + minutes : minutes) + ':' + (seconds < 10 ? '0' + seconds : seconds);
    } catch (error) {}
  }

  async getStudents() {
    try {
      return await firstValueFrom(this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Student}`));
    } catch (error) {
      throw error;
    }
  }

  async getStudentSubmittedAssessment(params) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_submitted_assessment}`, {
          params,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async getAssessmentById(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }

  removeStudentObservers() {
    try {
      for (const [index] of this.isExpand?.observers?.entries()) {
        if (index !== this.isExpand?.observers?.length - 1) {
          this.isExpand.observers[index]['isStopped'] = true;
          this.isExpand.observers[index]['closed'] = true;

          this.isExpand?.observers?.splice(index, 1);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  async getStudentExtraHelpByClassroomIdAndCorrectNess(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.student_extra_help_by_student_and_correctness}`,
          {
            params: queryParams,
          },
        ),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async getStudentById(body: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_academic_details}`, {
          params: body,
        }),
      );
      return this.httpService.decryptToken(token);
    } catch (error) {
      throw error;
    }
  }

  async addStudent(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Student}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async updateExtraHelp(id: any, body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help}/${id}`, body),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async addStudentExtraHelp(body: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async getStudentExtraHelpByFromId(body: any) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help_by_from_id}`, {
          params: body,
        }),
      );
      if (body && !body.page) {
        return this.httpService.decodeUserToken(responseToken);
      } else {
        return this.httpService.decryptToken(responseToken);
      }
    } catch (error) {
      throw error;
    }
  }

  async getAiSolutionFromDatabase(body: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help_solution}`, {
          params: body,
        }),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async getStudentExtraHelp(body: any) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help}`, {
          params: body,
        }),
      );
      if (body && !body.page) {
        return this.httpService.decodeUserToken(responseToken);
      } else {
        return this.httpService.decryptToken(responseToken);
      }
    } catch (error) {
      throw error;
    }
  }

  async getStudentAssessment(body: any) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment}`, {
          params: body,
        }),
      );
      if (body && !body.currentPage) {
        return this.httpService.decodeUserToken(responseToken);
      } else {
        return this.httpService.decryptToken(responseToken);
      }
    } catch (error) {
      throw error;
    }
  }

  async getTeacherAssessmentById(assessment_id: number) {
    try {
      const response = await firstValueFrom(
        this.httpService.get(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment}/${assessment_id}`),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async addAssessment(body) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment}`, body),
      );
      return this.httpService.decryptToken(responseToken);
    } catch (error) {
      throw error;
    }
  }

  async getStudentAssessmentQuestions(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_teacher_assesment}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async addAssessmentQuestionEvaluation(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.assessment_question_evaluation}`, body),
      );
    } catch (error) {
      throw error;
    }
  }
  async updateAssessmentQuestionEvaluation(id, body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.assessment_question_evaluation}/${id}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }
  async checkTeacherActivatedAssessments(body) {
    try {
      const assessmentToken = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_activated_assessments}`, body),
      );

      return this.httpService.normalDecode(assessmentToken);
    } catch (error) {
      throw error;
    }
  }

  async updateAssessment(id, body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async addStudentSubmittedAssessment(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_submitted_assessment}`, body),
      );
    } catch (error) {
      throw error;
    }
  }
  async addStudentAssessmentNotes(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.assessment_question_student_note}`, body),
      );
    } catch (error) {
      throw error;
    }
  }
  async updateStudentAssessmentNotes(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.assessment_question_student_note}/${id}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }
  async getPreviousAssessmentNotes(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.previous_question_student_note}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async updateStudentSubmittedAssessment(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.students_submitted_assessment}/${id}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async studentByClassroomId(body) {
    try {
      const responseToken = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.StudentByClassroomId}`, body),
      );

      if (body && !body.page) {
        return this.httpService.decodeUserToken(responseToken);
      } else {
        return this.httpService.decodeTokenPagination(responseToken);
      }
    } catch (error) {
      throw error;
    }
  }

  async getStudentByUserId(queryParams: object) {
    try {
      const student = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.StudentByUserId}`, {
          params: queryParams,
        }),
      );

      return this.httpService.decodeToken(student);
    } catch (error) {
      throw error;
    }
  }

  async getStudentWithClassrooms(queryParams: object) {
    try {
      const student = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.getStudentWithClassrooms}`, {
          params: queryParams,
        }),
      );
      return student;
      // return this.httpService.decodeToken(student);
    } catch (error) {
      throw error;
    }
  }

  async updateStudent(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Student}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async studentParentValidation(body: object) {
    try {
      return firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.parent_student_management}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async addStudentParentValidation(body: object) {
    try {
      return firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.parent_student_management_post_single}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async deleteMultipleParentStudents(body: object) {
    try {
      return firstValueFrom(
        this.httpService.post(
          `${environment.XANO_SERVER_URL}${EndPoint.delete_multiple_parent_student_management}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async getParentStudentsValidation(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.parent_student_management}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async updateParentStudentValidation(id, body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.parent_student_management}/${id}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }
  async getStudentsValidation(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_school_transfer}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
      // return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }
  async updateStudentValidation(id, body) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.students_school_transfer}/${id}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }
  async updateStudentByUserID(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_by_user_id_and_patch}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async checkStudentSubmittedAssignment(body) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.check_if_student_submitted_assessment}`,
          body,
        ),
      );
    } catch (error) {
      throw error;
    }
  }
  async teacherAssessmentForEvalution(body: object) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.teacher_assesment_for_evaluation}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async getStudentPreviousQuestion(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_previously_asked_students_question}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async deleteStudentPreviousQuestion(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_extra_help}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }
  async getStudentDetailsByClassroomCodes(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_list_by_classroom_code}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async getAssignmentandAssessmentByStudentId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_assessment_and_assignment_data_by_student_id}`,
          {
            params: queryParams,
          },
        ),
      );
      return response;
      // return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }
  async getStudentAssignmentSummaryData(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_assignment_data_for_summary}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getStudentsProjectData(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_students_project_data}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getClassroomOfStudents(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_classrooms_of_students}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
  async getStudentAssessmentSummaryData(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_student_assessmant_data_for_summary}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description
   * Check whether the student has any timing conflicts with already enrolled classroom periods. The API returns `true` if a conflict (overlap) exists, otherwise `false`.
   *
   * @param {any} data - The payload containing joining period details for validation.
   *
   * @returns {Promise<boolean>}
   * Resolves to:
   * - `true` → conflict found (student cannot join)
   * - `false` → no conflict (safe to join)
   */
  async checkStudentClassroomConflicts(data): Promise<boolean> {
    const response = await firstValueFrom(
      this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.check_student_classroom_overlap}`, data),
    );
    return response;
  }
}
